/**
 * 5b: trial-invite token hand-off (review MEDIUM-2).
 *
 * The admin link is `/register?invite=<token>` and the token is a single-use
 * bearer secret, so it must not stay in the address bar where GTM tags,
 * Sentry and request Referers read it. `public/invite-capture.js` moves it
 * into sessionStorage before any of them run; `Register` reads it from there.
 *
 * @see docs/specifications/2026-09-30-trial-invite-bez-proformy/spec.md
 */

/** sessionStorage key — must match STORAGE_KEY in public/invite-capture.js. */
export const TRIAL_INVITE_TOKEN_KEY = "trialInviteToken";

const INVITE_PARAM = "invite";

/** Same cap as public/invite-capture.js — far above any real token (43 chars). */
const MAX_RAW_LENGTH = 512;

/**
 * The charset and length the backend accepts for `inviteToken`. Mirrored by
 * `TRIAL_INVITE_TOKEN_PATTERN`/`TRIAL_INVITE_TOKEN_MAX_LENGTH` in
 * `apps/backend/src/subscriptions/constants/trial-invite-token.constants.ts`
 * — change both (and the complement in TRAILING_NON_TOKEN_CHARS below).
 */
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/** What messengers and mail clients glue onto a link: `.`, `)`, `>`, `,`, quotes… */
const TRAILING_NON_TOKEN_CHARS = /[^A-Za-z0-9_-]+$/;

/**
 * The invite as the wizard sees it (review MEDIUM-1): no invite at all, a
 * usable token, or a value that is present but cannot be a token (damaged
 * link) — the last one must stop the wizard, not silently fall back to a
 * regular registration with a proforma.
 */
export type TrialInviteLink =
  | { status: "absent" }
  | { status: "token"; token: string }
  | { status: "malformed" };

/**
 * Parse a raw invite value. Trailing characters outside the token charset are
 * trimmed first (an autolinker or messenger often appends `.`, `)` or `>`);
 * anything still outside the charset/length the backend accepts is malformed.
 */
export const parseTrialInviteToken = (raw: string | null): TrialInviteLink => {
  if (raw === null) return { status: "absent" };
  const token = raw.trim().replace(TRAILING_NON_TOKEN_CHARS, "");
  return TOKEN_PATTERN.test(token)
    ? { status: "token", token }
    : { status: "malformed" };
};

/**
 * Fallback for when invite-capture.js did not run (blocked, cached old
 * index.html, storage disabled): take `?invite=` from the URL, store it and
 * strip it from the address bar. Returns `null` when the URL has none.
 */
const takeInviteFromUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(INVITE_PARAM);
  if (raw === null) return null;

  const capped = raw.slice(0, MAX_RAW_LENGTH);
  try {
    sessionStorage.setItem(TRIAL_INVITE_TOKEN_KEY, capped);
  } catch {
    // Storage disabled: the caller keeps the value in memory.
  }
  params.delete(INVITE_PARAM);
  const search = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`
  );
  return capped;
};

/**
 * The raw invite value for this tab — as it came, not validated — or `null`
 * when there is no invite. A `?invite=` still in the URL wins over a value
 * stored earlier in this tab (it is the link the user just opened).
 */
export const takeTrialInviteRaw = (): string | null => {
  const fromUrl = takeInviteFromUrl();
  if (fromUrl !== null) return fromUrl;
  try {
    return sessionStorage.getItem(TRIAL_INVITE_TOKEN_KEY);
  } catch {
    return null;
  }
};

/** Forget the invite (after a successful registration, or when declined). */
export const clearTrialInviteToken = (): void => {
  try {
    sessionStorage.removeItem(TRIAL_INVITE_TOKEN_KEY);
  } catch {
    // Storage disabled: nothing was stored.
  }
};
